import createClient, { type Middleware } from "openapi-fetch";
import { env } from "@/shared/config/env";
import { toast } from "@/shared/ui/toast";
import { readCsrfToken } from "./csrf";
import { errorMessage } from "./error-messages";
import { ApiError, toApiError } from "./errors";
import { createApiFetch } from "./fetch";
import type { paths } from "./schema";
import { notifySessionExpired } from "./session-events";

/** Same origin as the page: the dev proxy and the production reverse proxy both serve /api/v1. */
export const apiBaseUrl = new URL(env.VITE_API_BASE_URL, window.location.origin)
  .toString()
  .replace(/\/$/, "");

export const apiFetch = createApiFetch({
  refreshUrl: `${apiBaseUrl}/auth/refresh`,
  getCsrf: readCsrfToken,
  onSessionExpired: notifySessionExpired,
  onRateLimited: ({ code, retryAfter }) => {
    // ACCOUNT_LOCKED is shown by the login form itself.
    if (code === "RATE_LIMITED") {
      toast.error(
        "Too many requests",
        retryAfter ? `Try again in ${retryAfter} seconds.` : "Please wait a moment.",
      );
    }
  },
});

/** Normalises every non-2xx response to an ApiError. */
const errorMiddleware: Middleware = {
  async onResponse({ response }) {
    if (!response.ok) throw await toApiError(response);
    return response;
  },
};

export const api = createClient<paths>({
  baseUrl: apiBaseUrl,
  credentials: "include",
  fetch: (request) => apiFetch(request),
});
api.use(errorMiddleware);

export const refreshSession = () => apiFetch.refresh();

export { ApiError, errorMessage };
